import classNames from "classnames";
import React from "react";
import { LazyLoadImage, StarRating, Link } from "../../../misc";
import { DraggingContext } from "../../../utils";

import "./ReviewCard.scss";

interface Props {
  content: IContentReview;
  className: string;
  renderTags: (content: IContent) => JSX.Element;
  isHero: boolean;
  delayClassIndex: number;
  tabIndex?: number;
  onClick?: () => void;
  url: string;
}

/**
 * A card to display a review entry with a link to the entry's page.
 *
 * @status stable
 */
export const ReviewCard = ({
  content,
  className,
  renderTags,
  isHero,
  delayClassIndex,
  onClick,
  tabIndex,
  url
}: Props) => {
  if (!content) {
    return null;
  }

  const review = content.content;
  let heroImageUrl;
  let heroImageMeta;
  if (content.heroImage) {
    heroImageUrl = content.heroImage.largeUrl;
    heroImageMeta = content.heroImage.meta;
  } else {
    const destination = content.locations && content.locations[0];
    heroImageUrl = destination?.heroImage?.largeUrl || "/image/missing-image.jpg";
    heroImageMeta = destination?.heroImage?.meta || "";
  }

  return (
    <div className={classNames(className, {
      "card-type-hero": isHero,
    })}>
      {isHero &&
        <div className="image-container">
          <LazyLoadImage src={heroImageUrl} alt={heroImageMeta} />
        </div>
      }

      <div className="card-body-wrapper">
        <div className="card-body">
          <div className="card-title-wrap">
            <StarRating
              className="card-star-rating"
              currentValue={review.overallRating}
              numberOfStars={review.overallRating}
              maximumValue={review.overallRating}
            />
            <h3 className="card-title text--dark">{content.title}</h3>
          </div>

          <div className="card-copy">
            {content.meta.descriptionTag &&
              <p className="card-description text--regular">
              &quot;{content.meta.descriptionTag}&quot;
              </p>
            }
          </div>
          <div className="card-tags">
            {renderTags(content)}
          </div>
        </div>
      </div>

      <DraggingContext.Consumer>
        {props => <Link
          {...props}
          className="card-link"
          to={url}
          tabIndex={tabIndex || 0}
          onClick={onClick}
        />}
      </DraggingContext.Consumer>
    </div>
  );
};
